import { Helmet } from 'react-helmet-async';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import ClientLogos from '@/components/ClientLogos';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Building, Truck, Users, Shield, Gavel, Heart, Phone, Calendar } from 'lucide-react';
import { openCalendlyPopup } from '@/utils/calendly';
import WhatsAppButton from '@/components/WhatsAppButton';
const Zakelijk = () => {
  const businessInsurances = [{
    icon: Building,
    title: 'Bedrijfsverzekeringen',
    description: 'Bescherm je bedrijf, inventaris en gebouwen tegen alle mogelijke risico\'s.',
    features: ['Bedrijfsaansprakelijkheid', 'Inventarisverzekering', 'Gebouwenverzekering', 'Bedrijfsschadeverzekering']
  }, {
    icon: Truck,
    title: 'Bedrijfsautoverzekering',
    description: 'Verzeker je bedrijfswagens, vrachtwagens of complete wagenpark.',
    features: ['Vlootpolissen', 'Vrachtvervoer verzekering', 'Cascoverzekering', '24/7 pechservice']
  }, {
    icon: Users,
    title: 'Werknemersverzekeringen',
    description: 'Zorg goed voor je werknemers met collectieve verzekeringen.',
    features: ['Collectieve zorgverzekering', 'WGA-Hiaatverzekering', 'Ziekteverzuimverzekering', 'Aansprakelijkheidsverzekering']
  }, {
    icon: Shield,
    title: 'Cyberverzekering',
    description: 'Bescherm je bedrijf tegen cybercriminaliteit en dataschending.',
    features: ['Dataverlies dekking', 'Cyberaanvallen', 'Privacy schending', 'Business interruption']
  }, {
    icon: Gavel,
    title: 'Rechtsbijstandverzekering',
    description: 'Juridische bijstand voor alle bedrijfsgerelateerde geschillen.',
    features: ['Contractgeschillen', 'Arbeidsrecht', 'Fiscaal recht', '24/7 juridisch advies']
  }, {
    icon: Heart,
    title: 'Directeurenverzekering',
    description: 'Speciale verzekeringen voor directeuren en bestuurders.',
    features: ['D&O verzekering', 'Keyman verzekering', 'Management liability', 'Compagnonsverzekering']
  }];
  return <div className="min-h-screen">
      <Helmet>
        <title>Zakelijke Verzekeringen - Compleet Verzekerd</title>
        <meta name="description" content="Zakelijke verzekeringen: bedrijfsverzekeringen, bedrijfsauto's, werknemers, cyber en rechtsbijstand. Persoonlijk advies voor ondernemers!" />
        <meta name="keywords" content="zakelijke verzekeringen, bedrijfsverzekering, bedrijfsauto, werknemersverzekering, cyberverzekering" />
        <link rel="canonical" href="https://compleetverzekerd.nl/zakelijk/" />
      </Helmet>
      <Header />
      
      {/* Hero Section */}
      <section className="bg-secondary text-secondary-foreground py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl">
            <h1 className="text-5xl font-bold mb-6">
              Zakelijke verzekeringen
            </h1>
            <p className="text-xl mb-8">
              Bescherm je bedrijf met de juiste verzekeringen. Van start-up tot gevestigd bedrijf, 
              wij hebben de expertise om je onderneming optimaal te verzekeren.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button size="lg" className="bg-primary hover:bg-primary-dark text-primary-foreground" onClick={openCalendlyPopup}>
                <Calendar className="h-5 w-5" />
                Plan een afspraak
              </Button>
              <Button variant="outline-inverse" size="lg" asChild>
                <a href="tel:0782200021">
                  <Phone className="h-5 w-5" />
                  078 - 220 00 21
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Business Insurance Types */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-secondary mb-4">
              Onze zakelijke verzekeringen
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Complete bescherming voor jouw bedrijf en werknemers
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {businessInsurances.map((insurance, index) => {
            const Icon = insurance.icon;
            return <Card key={index} className="p-6 hover:shadow-card transition-all duration-300 h-full">
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
                      <Icon className="w-8 h-8 text-primary-foreground" />
                    </div>
                    <h3 className="text-xl font-bold text-secondary mb-3">
                      {insurance.title}
                    </h3>
                    <p className="text-muted-foreground">
                      {insurance.description}
                    </p>
                  </div>

                  <div className="space-y-2 mb-6">
                    {insurance.features.map((feature, featureIndex) => <div key={featureIndex} className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-primary rounded-full"></div>
                        <span className="text-sm">{feature}</span>
                      </div>)}
                  </div>

                  
                </Card>;
          })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gradient-subtle">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-secondary mb-4">
              Waarom Compleet Verzekerd voor je bedrijf?
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl text-primary-foreground font-bold">1</span>
              </div>
              <h3 className="text-xl font-bold text-secondary mb-3">Bedrijfsspecialist</h3>
              <p className="text-muted-foreground">
                Onze adviseurs zijn gespecialiseerd in zakelijke verzekeringen en kennen de markt door en door.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl text-primary-foreground font-bold">2</span>
              </div>
              <h3 className="text-xl font-bold text-secondary mb-3">Persoonlijke service</h3>
              <p className="text-muted-foreground">
                Vaste contactpersoon die je bedrijf kent en begrijpt. Altijd bereikbaar voor al je vragen.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl text-primary-foreground font-bold">3</span>
              </div>
              <h3 className="text-xl font-bold text-secondary mb-3">Scherpe tarieven</h3>
              <p className="text-muted-foreground">
                Door onze lange ervaring en goede relaties krijgen we de beste tarieven voor onze klanten.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Client Logos Carousel */}
      <ClientLogos />

      {/* CTA Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-secondary mb-6">
            Klaar om je bedrijf beter te verzekeren?
          </h2>
          <p className="text-muted-foreground mb-8 max-w-2xl mx-auto text-base">
            Je kunt een afspraak inplannen via de website of even bellen met een van onze medewerkers. Je krijgt geen keuze menu maar gewoon contact met een adviseur of assistent. De afspraak kan plaatsvinden bij ons op kantoor of online via een videogesprek. Een gesprek duurt meestal 15 tot 60 minuten, afhankelijk van jouw situatie. Er worden geen kosten in rekening gebracht voor een adviesgesprek. In veel gevallen is een afspraak niet eens nodig en kunnen we je direct telefonisch al verder helpen.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-primary hover:bg-primary-dark" onClick={openCalendlyPopup}>
              <Calendar className="h-5 w-5" />
              Plan een afspraak
            </Button>
            <Button size="lg" variant="outline" asChild>
              <a href="tel:0782200021">
                <Phone className="h-5 w-5" />
                078 - 220 00 21
              </a>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>;
};
export default Zakelijk;